import { useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AuthCallback from "@/components/AuthCallback";
import UpdateBanner from "@/components/UpdateBanner";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import OAuthAuthorize from "@/pages/OAuthAuthorize";
import Dashboard from "@/pages/Dashboard";
import Ingredients from "@/pages/Ingredients";
import TechnicalSheets from "@/pages/TechnicalSheets";
import Pricing from "@/pages/Pricing";
import Analytics from "@/pages/Analytics";
import Events from "@/pages/Events";
import ShoppingList from "@/pages/ShoppingList";
import MeasureConversions from "@/pages/MeasureConversions";
import Stock from "@/pages/Stock";
import AttackPlan from "@/pages/AttackPlan";
import ModuleCenter from "@/pages/ModuleCenter";
import DREPage from "@/pages/DREPage";
import Suppliers from "@/pages/Suppliers";
import ProductionOrders from "@/pages/ProductionOrders";
import WasteControl from "@/pages/WasteControl";
import TeamManagement from "@/pages/TeamManagement";
import MenuEngineering from "@/pages/MenuEngineering";
import MenuBuilder from "@/pages/MenuBuilder";
import BIDashboard from "@/pages/BIDashboard";
import Checklists from "@/pages/Checklists";
import ChefMemoryPanel from "@/pages/ChefMemoryPanel";
import Pracas from "@/pages/Pracas";
import ProfilePage from "@/pages/ProfilePage";
import PublicMenu from "@/pages/PublicMenu";
import SharedItemView from "@/pages/SharedItemView";
import JoinTeam from "@/pages/JoinTeam";
import Layout from "@/components/Layout";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRouter() {
  const location = useLocation();

  // CRITICAL: Detect session_id synchronously during render (before ProtectedRoute runs)
  if (location.hash?.includes('session_id=') || location.search?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/menu/:shareToken" element={<PublicMenu />} />
      <Route path="/share/:code" element={<SharedItemView />} />
      <Route path="/join/:token" element={<JoinTeam />} />
      <Route path="/oauth/authorize" element={<OAuthAuthorize />} />
      
      {/* Protected routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<Dashboard />} />
        <Route path="ingredients" element={<Ingredients />} />
        <Route path="technical-sheets" element={<TechnicalSheets />} />
        <Route path="pricing" element={<Pricing />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="events" element={<Events />} />
        <Route path="shopping-list" element={<ShoppingList />} />
        <Route path="measure-conversions" element={<MeasureConversions />} />
        <Route path="stock" element={<Stock />} />
        <Route path="attack-plan" element={<AttackPlan />} />
        <Route path="module-center" element={<ModuleCenter />} />
        <Route path="dre" element={<DREPage />} />
        <Route path="suppliers" element={<Suppliers />} />
        <Route path="production-orders" element={<ProductionOrders />} />
        <Route path="waste-control" element={<WasteControl />} />
        <Route path="team" element={<TeamManagement />} />
        <Route path="menu-engineering" element={<MenuEngineering />} />
        <Route path="menu-builder" element={<MenuBuilder />} />
        <Route path="bi-dashboard" element={<BIDashboard />} />
        <Route path="checklists" element={<Checklists />} />
        <Route path="pracas" element={<Pracas />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="chef-memory" element={<ChefMemoryPanel />} />
      </Route>
    </Routes>
  );
}

import SplashScreen from "@/components/SplashScreen";

function App() {
  const [showSplash, setShowSplash] = useState(() => {
    // Skip splash for share/public routes and returning sessions
    if (sessionStorage.getItem("sop_splash_shown")) return false;
    if (window.location.pathname.startsWith("/share/")) return false;
    if (window.location.pathname.startsWith("/menu/")) return false;
    return true;
  });

  const handleSplashComplete = useCallback(() => {
    sessionStorage.setItem("sop_splash_shown", "1");
    setShowSplash(false);
  }, []);

  return (
    <div className="App">
      {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
      <AuthProvider>
        <LanguageProvider>
        <BrowserRouter>
          <UpdateBanner />
          <AppRouter />
        </BrowserRouter>
        </LanguageProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
